import { Skeleton } from "@/components/ui/skeleton";
import { GlassCard, GlassCardContent } from "@/components/ui/glass-card";
import { useIsMobile } from "@/hooks/use-mobile";

/**
 * Compact loading skeleton that mirrors the DeviceHeader + tabs layout
 * of the Device Details page so mobile users don't see a tall blank
 * spinner area while the fetch is in flight.
 */
export const DeviceDetailSkeleton = () => {
  const isMobile = useIsMobile();

  return (
    <div className="space-y-6" aria-busy="true" aria-label="Loading device details">
      {/* Title row */}
      <div className="flex items-center gap-3">
        <Skeleton className="h-8 w-8 rounded" />
        <div className="space-y-2 flex-1 min-w-0">
          <Skeleton className="h-5 w-48 max-w-full" />
          <Skeleton className="h-3 w-32 max-w-full" />
        </div>
        <Skeleton className="h-8 w-20 rounded" />
      </div>

      {/* Tag row */}
      <div className="flex gap-2 flex-wrap">
        <Skeleton className="h-7 w-24 rounded-full" />
        <Skeleton className="h-7 w-20 rounded-full" />
      </div>

      {/* Header stat cards — match compact 3-col mobile / 3-col desktop layout */}
      <div className="grid grid-cols-3 gap-2 md:gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <GlassCard key={i} variant="subtle" hover={false}>
            <GlassCardContent className={isMobile ? "p-3" : "p-6"}>
              <Skeleton className={isMobile ? "h-3 w-16 mb-2" : "h-4 w-24 mb-3"} />
              <Skeleton className={isMobile ? "h-5 w-12" : "h-7 w-20"} />
              {!isMobile && <Skeleton className="h-3 w-32 mt-3" />}
            </GlassCardContent>
          </GlassCard>
        ))}
      </div>

      {/* Tabs strip */}
      <div className="flex gap-2 border-b border-border pb-2">
        {Array.from({ length: isMobile ? 3 : 5 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-24 rounded" />
        ))}
      </div>

      {/* Tab body placeholder */}
      <div className="space-y-3">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  );
};

export default DeviceDetailSkeleton;
